import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ProblemDetails } from '@/@types/base.types.ts';
import { useAxiosAuth } from '@/hooks/useAxiosAuth.ts';
import { ApiConfig } from '@/config/api.config.ts';
import EToast from '@/components/everridge/EToast';
import { useTranslation } from 'react-i18next';

type Props = {
    showToast?: boolean;
};

export const useOwnerInvatationApi = ({ showToast = true }: Props = {}) => {
    const { t } = useTranslation();
    const axiosInstance = useAxiosAuth();
    const queryClient = useQueryClient();

    const { mutateAsync: sendInvitationEmail, isPending: isDoSendInvitationEmailPending } =
        useMutation({
            mutationFn: async (ownerId: string) => {
                const url = ApiConfig.queries.features.owners.endpoints.sendInvitation.replace(
                    '{ownerId}',
                    ownerId,
                );
                const response = await axiosInstance.get(url);
                return response.data;
            },
            onSuccess: (_, ownerId) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                showToast &&
                    EToast({
                        message: t('main.owners.toasts.invitation.success.message'),
                        description: t('main.owners.toasts.invitation.success.description'),
                        variant: 'success',
                    });
                queryClient.invalidateQueries({
                    queryKey: [ApiConfig.queries.features.owners.queryKeys.details, ownerId],
                });
            },
            onError: (error: AxiosError<ProblemDetails>) => {
                console.error(error);

                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                showToast &&
                    EToast({
                        message: t('main.owners.toasts.invitation.error.message'),
                        description: t('main.owners.toasts.invitation.error.description'),
                        variant: 'error',
                    });
            },
        });

    return {
        sendInvitationEmail,
        isDoSendInvitationEmailPending,
    };
};
